/*
  How the profile screen asks to change the person's memory (`edit_my_memory`, `edit_admin_memory`: `{set, remove}`).

  The contract names the two fields and nothing more, so the form is fixed here, in one file, and is listed as a
  contract request in NOTES.md:
  - `set` has the schema's field names. A scalar is given as its new value (`detail_level`, `experience`,
    `used_in_other_discipline`). A map is given as the part to change (`concepts`, `modules`): the server merges it.
  - `remove` names one entry of a list as "<field>:<text>" (`preferences:...`, `remember:...`).
  The person can only raise a concept level; levels go down only when the memory is cleared (user-memory.md, section 2).
*/

import type { PersonMemory } from "../../../api/types";
import { CONCEPT_LEVELS, type ConceptLevel } from "../shared/words";

export interface MemoryEdit {
  set: Record<string, unknown>;
  remove: string[];
}

export const setDetailLevel = (level: PersonMemory["detail_level"]): MemoryEdit => ({ set: { detail_level: level }, remove: [] });

export const setExperience = (answer: NonNullable<PersonMemory["experience"]>): MemoryEdit => ({ set: { experience: answer }, remove: [] });

export const setUsedInOtherDiscipline = (answer: boolean): MemoryEdit => ({ set: { used_in_other_discipline: answer }, remove: [] });

export const raiseConcept = (concept: string, level: ConceptLevel): MemoryEdit => ({ set: { concepts: { [concept]: level } }, remove: [] });

export const setModuleStatus = (module: string, status: PersonMemory["modules"][string]): MemoryEdit => ({ set: { modules: { [module]: status } }, remove: [] });

export const forgetPreference = (text: string): MemoryEdit => ({ set: {}, remove: [`preferences:${text}`] });

export const forgetRemembered = (text: string): MemoryEdit => ({ set: {}, remove: [`remember:${text}`] });

export function levelRank(level: string): number {
  const index = (CONCEPT_LEVELS as readonly string[]).indexOf(level);
  return index < 0 ? 0 : index;
}

/** A level can be chosen when it is the same or higher than the one now. */
export function canChooseLevel(current: string, wanted: ConceptLevel): boolean {
  return levelRank(wanted) >= levelRank(current);
}
